import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import "./CabBookingModal.css";

const CabBookingModal = ({ isOpen, toggle, cab, searchData }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Name must be at least 3 characters";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Create booking object
    const booking = {
      id: `CAB${Date.now()}`,
      customer: {
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
      },
      cab: {
        id: cab.id,
        name: cab.name,
        type: cab.type,
        pricePerKm: cab.pricePerKm,
        basePrice: cab.basePrice,
      },
      trip: {
        pickup: searchData?.pickup || "",
        drop: searchData?.drop || "",
        date: searchData?.date || "",
        time: searchData?.time || "",
      },
      status: "confirmed",
      bookedAt: new Date().toISOString(),
    };

    // Get existing bookings from localStorage
    const existingBookings = JSON.parse(
      localStorage.getItem("cabBookings") || "[]"
    );

    // Add new booking
    existingBookings.push(booking);

    // Save to localStorage
    localStorage.setItem("cabBookings", JSON.stringify(existingBookings));

    setTimeout(() => {
      setIsSubmitting(false);
      toggle();
      // Navigate to thank you page with booking details
      navigate("/cab-booking-success", { state: { booking } });
    }, 1000);
  };

  const handleClose = () => {
    setFormData({ fullName: "", phone: "", email: "" });
    setErrors({});
    toggle();
  };

  if (!cab) return null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="cab__booking__modal" centered>
      <ModalHeader toggle={handleClose}>
        <i className="ri-car-line"></i> Complete Your Booking
      </ModalHeader>
      <ModalBody>
        <div className="booking__cab__info">
          <div className="booking__cab__image">
            <img src={cab.photo} alt={cab.name} />
          </div>
          <div className="booking__cab__details">
            <h5>{cab.name}</h5>
            <span className="cab__type">{cab.type}</span>
            <div className="trip__info">
              <p>
                <i className="ri-map-pin-line"></i> {searchData?.pickup} → {searchData?.drop}
              </p>
              <p>
                <i className="ri-calendar-line"></i> {searchData?.date} at {searchData?.time}
              </p>
            </div>
            <div className="booking__price">
              <span>Base Fare: ₹{cab.basePrice}</span>
              <span>Per KM: ₹{cab.pricePerKm}</span>
            </div>
          </div>
        </div>

        <Form onSubmit={handleSubmit} className="booking__form">
          <FormGroup>
            <Label for="fullName">
              <i className="ri-user-line"></i> Full Name *
            </Label>
            <Input
              type="text"
              name="fullName"
              id="fullName"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={handleChange}
              className={errors.fullName ? "is-invalid" : ""}
            />
            {errors.fullName && (
              <span className="error__message">{errors.fullName}</span>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="phone">
              <i className="ri-phone-line"></i> Phone Number *
            </Label>
            <Input
              type="tel"
              name="phone"
              id="phone"
              placeholder="Enter your 10-digit phone number"
              value={formData.phone}
              onChange={handleChange}
              className={errors.phone ? "is-invalid" : ""}
            />
            {errors.phone && (
              <span className="error__message">{errors.phone}</span>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="email">
              <i className="ri-mail-line"></i> Email Address *
            </Label>
            <Input
              type="email"
              name="email"
              id="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? "is-invalid" : ""}
            />
            {errors.email && (
              <span className="error__message">{errors.email}</span>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="confirm__booking__btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line spin"></i> Processing...
            </>
          ) : (
            <>
              <i className="ri-check-line"></i> Confirm Booking
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CabBookingModal;
